/**
 * refreshManager — handles token refresh with concurrency control.
 *
 * Problem it solves:
 *   When multiple requests fail with 401 simultaneously, each would independently
 *   try to call /auth/refresh, causing a race condition that invalidates the
 *   refresh token on the second call (rotation). This module ensures only ONE
 *   refresh call is ever in-flight; all other callers wait on the same promise.
 *
 * Flow:
 *   1. First 401 (auth) → starts refresh, stores the promise.
 *   2. Subsequent 401s while refreshing → receive the same promise (queue).
 *   3. On success → all queued requests resolve and retry with the new token.
 *   4. On failure → fires 'session-expired' CustomEvent → SessionExpiredDialog shows.
 *
 * Non-auth 401s (insufficient permissions on a specific resource) are handled
 * in api.js before reaching this module and should NOT trigger session expiry.
 * Those should return 403; if a server incorrectly returns 401 with a known
 * permission-specific code, api.js filters them out first.
 */

import axios from 'axios';
import tokenManager from './tokenManager';
import { API_CONFIG } from './config';
import { SESSION_EXPIRED_EVENT, SESSION_RESTORED_EVENT } from '../components/auth/SessionExpiredDialog';

// ── State ───────────────────────────────────────────────────────────────────
// The single in-flight refresh promise. null when no refresh is running.
let _refreshPromise = null;

// Guards against stacking multiple 'session-expired' events when several
// concurrent requests all fail simultaneously after the refresh token expires.
// Reset by resetExpiredGuard() (called from AuthContext after fresh login).
let _expiredFired = false;

// ── Token refresh ────────────────────────────────────────────────────────────
/**
 * Attempt to refresh the access token.
 * Uses a raw axios call (not the api instance) to avoid interceptor loops.
 * The server reads the httpOnly refresh-token cookie automatically.
 * Returns the new access token string on success, throws on failure.
 */
const executeRefresh = async () => {
  const response = await axios.post(
    `${API_CONFIG.baseURL}/auth/refresh`,
    {},
    {
      timeout: 10000,
      withCredentials: true, // sends the httpOnly refresh-token cookie
    }
  );

  const newToken = response.data?.token || response.data?.accessToken;
  if (!newToken) throw new Error('No access token in refresh response');

  tokenManager.set(newToken);
  return newToken;
};

const refreshManager = {
  /**
   * Call this when a 401 is received on an authenticated request.
   * Guarantees only one /auth/refresh is in-flight at a time.
   * All concurrent callers share the same promise and receive the same result.
   */
  refresh() {
    if (_refreshPromise) {
      return _refreshPromise; // join the in-flight promise
    }

    _refreshPromise = executeRefresh()
      .finally(() => {
        _refreshPromise = null;
      });

    return _refreshPromise;
  },

  /** True while a refresh is in-flight. */
  isRefreshing() {
    return _refreshPromise !== null;
  },

  /**
   * Called when the session is definitively invalid:
   *   - The refresh token is expired or revoked (refresh call failed)
   *   - The inactivity threshold was exceeded (we skipped refresh entirely)
   *
   * This is NOT called for resource-level 401s that represent permissions
   * issues on a specific endpoint — those are filtered in api.js first.
   *
   * Fires a CustomEvent instead of hard-redirecting, giving SessionExpiredDialog
   * a chance to show the user a polished notification and preserve their
   * page context.
   *
   * @param {'expired'|'inactivity'|'revoked'} reason - shown in dialog
   */
  handleAuthFailure(reason = 'expired') {
    tokenManager.clear();
    localStorage.removeItem('token'); // clean up any legacy stored token

    // Deduplicate: if multiple concurrent requests all fail, only show
    // the dialog once per session lifecycle.
    if (_expiredFired) return;
    _expiredFired = true;

    // Never show the dialog on public pages — the user is already
    // unauthenticated and there's nothing meaningful to say.
    const path = window.location.pathname;
    const isPublic = ['/login', '/', '/home', '/register', '/forgot-password'].includes(path)
      || path.startsWith('/blog')
      || path.startsWith('/partner/login')
      || path.startsWith('/forgot-password')
      || path.startsWith('/stepwise-register');

    if (isPublic) {
      _expiredFired = false; // reset so a re-login can complete the guard
      return;
    }

    // Fire event — SessionExpiredDialog (mounted globally) will handle it.
    window.dispatchEvent(
      new CustomEvent(SESSION_EXPIRED_EVENT, { detail: { reason } })
    );
  },

  /**
   * Reset the expiry guard after a successful fresh login.
   * Must be called from AuthContext whenever authentication succeeds so that
   * a future real expiry in the same SPA lifecycle will show the dialog again.
   *
   * Also broadcasts SESSION_RESTORED_EVENT so other tabs can dismiss their
   * expiry dialogs.
   */
  resetExpiredGuard() {
    _expiredFired = false;
    // Notify other same-origin tabs (BroadcastChannel is handled in
    // SessionExpiredDialog; the window event handles the current tab).
    window.dispatchEvent(new CustomEvent(SESSION_RESTORED_EVENT));
  },
};

export default refreshManager;
